// GLOBAL TEMP (top bar): the carbon meter as a temperature bar 0-3 degrees with ticks at 1 and 2
// (the sea rises at each tick; 3 degrees ends the game), and two small gauges under the idea of
// "who can cancel what": the CITY SINK (green tiles; cancels the city's emissions only) and the
// SHARED RESERVE (Climate Bank and cooling global cards; cancels any emission).
// The whole meter is public. Every number comes from `game.carbon` (server, never computed here).

const KICK = { fontSize: 9.5, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: "var(--ink-4)", lineHeight: "11px" };

function Gauge({ testid, label, value, color, hint }) {
  // A small pill: a dot, the label and the number. It has no maximum, so it is a count, not a bar.
  return (
    <span data-testid={testid} data-value={value} title={hint} className="inline-flex items-center" style={{ gap: 4, fontSize: 11, lineHeight: "13px", color: "var(--ink-2)" }}>
      <span aria-hidden="true" style={{ width: 8, height: 8, borderRadius: 4, background: color, opacity: value > 0 ? 1 : 0.35 }} />
      {label} <b className="tabular-nums" style={{ color: "var(--ink)" }}>{value}</b>
    </span>
  );
}

export function carbonTitle(c, events = 0) {
  const per = c.per_degree || 10;
  const top = c.max_degrees || 3;
  const r = c.round || {};
  const lg = c.last_global;
  const last = lg ? `Last global card: ${lg.name} (${lg.kind === "warming" ? `warms the meter by ${lg.amount}` : `adds ${lg.amount} to the shared reserve`}).` : "Last global card: none yet.";
  return [
    `Meter ${c.meter} of ${top * per} carbon (${per} per degree). The meter never goes down.`,
    `This round so far: city +${r.city || 0} (city sink cancelled ${r.by_city_sink || 0}, shared reserve ${r.by_reserve || 0}), the world adds +${c.world_per_round ?? 1} at the end of the round.`,
    last,
    `The sea rises at 1°, 2° and 3° (${events} of ${top} so far); ${top}° ends the game.`,
  ].join(" ");
}

export default function GlobalTemp({ game }) {
  const c = game.carbon;
  if (!c) return null;
  const top = c.max_degrees || 3;
  const per = c.per_degree || 10;
  const pending = c.pending_rises ?? game.slr_pending_count ?? 0;
  const fill = Math.max(0, Math.min(100, (c.meter / (top * per)) * 100));
  const ticks = Array.from({ length: top - 1 }, (_, i) => ((i + 1) / top) * 100);
  return (
    <div data-testid="global-temp" data-meter={c.meter} data-degrees={c.degrees} className="flex flex-col justify-center whitespace-nowrap shrink-0" title={carbonTitle(c, game.slr_events ?? 0)}>
      <span className="flex items-center" style={{ ...KICK, gap: 5 }}>
        Global temp
        {pending > 0 && <span data-testid="global-temp-pending" data-pending={pending} style={{ color: "var(--sea)" }}>· sea rises at round end</span>}
      </span>
      <span className="flex items-center" style={{ gap: 8, height: 19 }}>
        <span data-testid="global-temp-bar" role="meter" aria-label="Global temperature" aria-valuemin={0} aria-valuemax={top} aria-valuenow={c.temp}
          className="relative block overflow-hidden" style={{ width: 120, height: 8, borderRadius: 4, background: "var(--line)" }}>
          <span className="absolute left-0 top-0 bottom-0" style={{ width: `${fill}%`, background: "linear-gradient(90deg, var(--coin-border), #b5532e)", transition: "width 400ms ease" }} />
          {ticks.map((t) => (
            <span key={t} aria-hidden="true" className="absolute top-0 bottom-0" style={{ left: `${t}%`, width: 2, marginLeft: -1, background: "var(--card)" }} />
          ))}
        </span>
        <span data-testid="global-temp-value" className="tabular-nums" style={{ fontSize: 13, fontWeight: 700, color: "var(--ink)" }}>
          {Number(c.temp).toFixed(1)}°
        </span>
        <Gauge testid="city-sink-gauge" label="City sink" value={c.city_sink} color="var(--infra-green-top)"
          hint="City sink: carbon stored by green tiles. It cancels the city's emissions (floors and walls) only." />
        <Gauge testid="shared-reserve-gauge" label="Shared reserve" value={c.shared_reserve} color="var(--sea)"
          hint="Shared reserve: filled by Climate Bank projects and cooling global cards. It cancels any emission, including the world's." />
      </span>
    </div>
  );
}
